"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Annotator = void 0;
const fabric_with_gestures_1 = require("fabric-with-gestures");
const eventManager_1 = require("./eventManager");
const circleTool_1 = require("./tools/circleTool");
const drawingTool_1 = require("./tools/drawingTool");
const rectangleTool_1 = require("./tools/rectangleTool");
const textTool_1 = require("./tools/textTool");
const arrowTool_1 = require("./tools/arrowTool");
class Annotator {
    constructor(canvasId, fullScreen = false) {
        this.eventManager = new eventManager_1.EventManager();
        this.activeTool = null;
        this.annotating = false;
        this.fullScreen = false;
        this.scale = null;
        this.fullSize = null;
        this.init = (options) => {
            return new Promise((res, rej) => {
                fabric_with_gestures_1.fabric.Image.fromURL(options.imageUrl, (img) => {
                    this.fullSize = {
                        width: img.width || 0,
                        height: img.height || 0
                    };
                    if (img.width && img.height) {
                        if (img.width > img.height) {
                            this.fullSize.width = 1024;
                            this.fullSize.height = (1024 / img.width) * img.height;
                        }
                        else {
                            this.fullSize.height = 1024;
                            this.fullSize.width = (1024 / img.height) * img.width;
                        }
                    }
                    this.canvas.setHeight(this.fullSize.height);
                    this.canvas.setWidth(this.fullSize.width);
                    this.canvas.getElement().style.backgroundImage = "url(" + options.imageUrl + ")";
                    this.canvas.getElement().style.backgroundSize = "contain";
                    this.scale = this.canvas.width / this.fullSize.width;
                    this.canvas.loadFromJSON(options.json, () => {
                        if (options.readonly) {
                            this.canvas.selection = false;
                            this.canvas.getObjects().forEach(object => {
                                object.set('selectable', false);
                            });
                        }
                        this.canvas.renderAll.bind(this.canvas);
                    });
                    this.resizeCanvas();
                    res();
                });
            });
        };
        this.importJson = (json) => {
            this.canvas.loadFromJSON(json, this.canvas.renderAll.bind(this.canvas));
        };
        this.exportJson = () => {
            var json = this.canvas.toJSON();
            return json;
        };
        this.exportSvg = () => {
            var originalScale = this.scale;
            this.scaleCanvas(1);
            var svg = this.canvas.toSVG();
            this.scaleCanvas(originalScale);
            return svg;
        };
        this.on = (event, callback) => {
            this.eventManager.on(event, callback);
        };
        this.off = (event, callback) => {
            this.eventManager.off(event, callback);
        };
        this.trigger = (event, params) => {
            this.eventManager.trigger(event, params);
        };
        this.draw = (options) => {
            this.setTool(new drawingTool_1.DrawingTool(options));
        };
        this.arrow = (options) => {
            this.setTool(new arrowTool_1.ArrowTool(options));
        };
        this.text = (options) => {
            this.setTool(new textTool_1.TextTool(options));
        };
        this.rectangle = (options) => {
            this.setTool(new rectangleTool_1.RectangleTool(options));
        };
        this.clearTool = () => {
            this.setTool(null);
        };
        this.circle = (options) => {
            this.setTool(new circleTool_1.CircleTool(options));
        };
        this.deleteSelection = () => {
            this.canvas.getActiveObjects().forEach((obj) => {
                this.canvas.remove(obj);
            });
            this.canvas.discardActiveObject().renderAll();
        };
        this.resizeCanvas = (event) => {
            var aspectRatio = this.fullSize.width / this.fullSize.height;
            var maxWidth;
            var maxHeight;
            if (this.fullScreen) {
                maxWidth = window.innerWidth;
                maxHeight = window.innerHeight - this.canvas.getElement().parentElement.offsetTop;
            }
            else {
                maxWidth = this.canvas.getElement().parentElement.parentElement.clientWidth;
                maxHeight = this.canvas.getElement().parentElement.parentElement.clientHeight;
            }
            var resizedWidth = maxWidth;
            if ((resizedWidth) / aspectRatio > maxHeight) {
                resizedWidth = maxHeight * aspectRatio;
            }
            this.scaleCanvas(resizedWidth / this.fullSize.width);
        };
        this.setTool = (tool) => {
            if (this.activeTool) {
                this.activeTool.deactivate();
            }
            this.activeTool = tool;
            if (tool) {
                this.canvas.discardActiveObject().renderAll();
                tool.activate(this);
                this.trigger("tool:selected");
            }
            else {
                this.trigger("tool:cleared");
            }
        };
        this.registerProxyEventListeners = () => {
            this.canvas.on('selection:cleared', () => this.eventManager.trigger('selection:cleared'));
            this.canvas.on('selection:created', () => this.eventManager.trigger('selection:created'));
        };
        this.registerEventListeners = () => {
            this.on('init', (options) => this.init(options));
            this.on('tool:clear', (options) => this.setTool(null));
            this.on('tool:draw', (options) => this.draw(options));
            this.on('tool:arrow', (options) => this.arrow(options));
            this.on('tool:text', (options) => this.text(options));
            this.on('tool:rectangle', (options) => this.rectangle(options));
            this.on('tool:circle', (options) => this.circle(options));
            this.on('selection:delete', () => this.deleteSelection());
            this.on('export:svg', () => this.exportSvg());
            this.on('import:json', (json) => this.importJson(json));
            this.on('export:json', () => this.exportJson());
        };
        this.registerTouchEvents = () => {
            let zoomStartScale = 0;
            this.canvas.on('touch:gesture', (e) => {
                if (e.e.touches && e.e.touches.length == 2) {
                    var point = new fabric_with_gestures_1.fabric.Point(e.self.x, e.self.y);
                    if (e.self.state == "start") {
                        zoomStartScale = this.canvas.getZoom();
                    }
                    var delta = zoomStartScale * e.self.scale;
                    this.canvas.zoomToPoint(point, delta);
                }
            });
        };
        this.setDefaultControlsVisibility = () => {
            fabric_with_gestures_1.fabric.Textbox.prototype.setControlsVisibility({
                mt: false,
                mb: false,
                bl: false,
                br: false,
                tl: false,
                tr: false
            });
            fabric_with_gestures_1.fabric.Group.prototype.setControlsVisibility({
                tl: false,
                mt: false,
                tr: false,
                ml: false,
                mr: false,
                bl: false,
                mb: false,
                br: false
            });
        };
        this.scaleCanvas = (scale) => {
            var objects = this.canvas.getObjects();
            /*for (var i in objects) {
                if (objects[i].type !== 'path') {
                    objects[i].left = (objects[i].left / objects[i].scaleX) * scale;
                    objects[i].top = (objects[i].top / objects[i].scaleY) * scale;
                    objects[i].scaleX = scale;
                    objects[i].scaleY = scale;
                    objects[i].setCoords();
                }
            }*/
            if (this.scale != scale) {
                this.canvas.setZoom(scale);
                this.canvas.setWidth(this.fullSize.width * scale);
                this.canvas.setHeight(this.fullSize.height * scale);
                this.scale = scale;
            }
        };
        this.fullScreen = fullScreen;
        this.canvas = new fabric_with_gestures_1.fabric.Canvas(canvasId);
        this.canvas.setHeight(window.innerHeight);
        this.canvas.setWidth(window.innerWidth);
        this.registerProxyEventListeners();
        this.registerEventListeners();
        this.registerTouchEvents();
        this.setDefaultControlsVisibility();
        this.canvas.on('mouse:down', (options) => {
            if (this.activeTool && !this.canvas.getActiveObjects().length) {
                this.activeTool.onMouseDown();
                this.annotating = true;
            }
        });
        this.canvas.on('mouse:move', (options) => {
            if (this.annotating && this.activeTool) {
                this.activeTool.onMouseMove();
                this.canvas.requestRenderAll();
            }
        });
        this.canvas.on('mouse:up', (options) => {
            if (this.annotating && this.activeTool) {
                if (this.activeTool.onMouseUp()) {
                    var obj = this.canvas.getObjects().pop();
                    if (obj) {
                        this.canvas.setActiveObject(obj);
                        this.canvas.requestRenderAll();
                    }
                }
                this.setTool(null);
                this.annotating = false;
            }
        });
    }
}
exports.Annotator = Annotator;
